<template>
  <div
    id="page-wrapper"
    role="main"
  >
    <div class="wrap-container">
      <on-page-breadcrumbs :data="breadcrumbs" />
    </div>
    <div class="image-wrap article-page__hero-image">
      <epi-picture
        v-if="image"
        :use-picture="false"
        :fit-dimensions="false"
        :auto-aspect-ratio="false"
        :image="image"
        :sizes="dimensions"
        edit="Image"
      />
      <epi-picture
        v-if="brandingImage"
        :image="brandingImage"
        :use-picture="true"
        :auto-aspect-ratio="true"
        class="image-wrap__sub-branding"
        :fit-dimensions="true"
        :sizes="brandingDimensions"
      />
    </div>
    <div class="article-page ">
      <section class="article-page__hero">
        <preamble-block
          :prefix="prefix"
          :title="title"
          :preamble="preamble"
          :align="'center'"
        >
          <template
            v-if="displayPreambleLink"
            #preamble-link
          >
            <br>{{ preambleLinkPreLabel }}
            <a
              :href="preambleLink"
              class="preamble__link"
            >{{ preambleLinkLabel }}</a>
          </template>
        </preamble-block>

        <BylineShareComponent
          :author="author"
          :authordate="authorDate"
          :title="seoTitle || title"
          :description="seoDescription"
          size="no-grid"
        />
      </section>
      <section
        class="article-page__content"
        :class="!similiarLinks.length && 'wrap-container--has-margin-bottom'"
      >
        <div class="layout__info-content">
          <div v-if="supplierContentArea.length > 0">
            <component
              :is="block.component"
              v-for="block in supplierContentArea"
              :key="block.contentId"
              v-bind="block"
            />
          </div>

          <!-- TINY MCE / CONTENT UPPER -->
          <wysiwyg
            v-if="contentUpper && contentUpper.length || isEditable"
            v-edit="'contentUpper'"
            class="fade-in wysiwyg--contentarea"
            :content="contentUpper"
          />
          <!-- CONTENT AREA UPPER -->
          <div
            v-if="contentAreaUpper.length || (isEditable && contentAreaUpper.length)"
            v-edit="'ContentAreaUpper'"
            class="article-page__content-area-upper"
          >
            <component
              :is="block.component == 'RecipeBlock' ? 'article-recipe-block' : block.component | kebab"
              v-for="(block, index) in contentAreaUpper"
              :id="block.contentId"
              :key="block.contentId"
              v-bind="block"
              :size="block.component == 'AccordionBlock' && ($root.isSmallViewport ? 80 : 56)"
              :tracking="{
                listName: 'recept',
                listItemIndex: String(index),
                listLength: contentAreaUpper.length
              }"
            >
              <template
                v-if="block.component == 'AccordionBlock'"
                #content
              >
                <wysiwyg :content="block.content" />
              </template>
            </component>
          </div>
          <wysiwyg
            v-if="contentLower.length || isEditable"
            v-edit="'contentLower'"
            class="fade-in wysiwyg--contentarea"
            :content="contentLower"
          />
          <div
            v-if="contentAreaLower.length || (isEditable && contentAreaLower.length)"
            v-edit="'ContentAreaLower'"
            class="article-page__content-area-lower"
          >
            <component
              :is="block.component == 'RecipeBlock' ? 'article-recipe-block' : block.component | kebab"
              v-for="(block, index) in contentAreaLower"
              :id="block.contentId"
              :key="block.contentId"
              v-bind="block"
              :size="block.component == 'AccordionBlock' && ($root.isSmallViewport ? 80 : 56)"
              :tracking="{
                listName: 'recept',
                listItemIndex: String(index),
                listLength: contentAreaLower.length
              }"
            >
              <template
                v-if="block.component == 'AccordionBlock'"
                #content
              >
                <wysiwyg :content="block.content" />
              </template>
            </component>
          </div>
        </div>
      </section>
      <section
        v-if="similiarLinks.length"
        class="content-area-wrapper wrap-container related-items-section"
      >
        <h2 class="related-items-section__title">
          Upptäck mer
        </h2>
        <IcaRelatedItems
          align="left"
          :items="similiarLinks"
        />
      </section>
      <section
        v-if="titleRecipeContentArea.length || isEditable"
        class="content-area-wrapper wrap-container "
      >
        <sub-heading
          v-if="titleRecipeContentArea || isEditable"
          :v-edit-heading="'titleAutomatedContentUpper'"
          :heading="titleRecipeContentArea"
        />
        <block-grid
          v-if="recipeContentArea.length || (isEditable && recipeContentArea.length)"
          v-edit="'recipeContentArea'"
          :blocks="recipeContentArea"
          :tracking="{
            listName: titleRecipeContentArea,
            listLength: undefined
          }"
          @open-folder-modal="openFolderModal"
        />
      </section>
      <section
        v-if="itemLists.articles || isEditable"
        ref="articles"
        class="content-area-wrapper wrap-container "
      >
        <sub-heading
          v-if="titleAutomatedContentUpper || isEditable"
          :v-edit-heading="'titleAutomatedContentLower'"
          :heading="titleAutomatedContentUpper"
        />

        <block-grid
          v-if="(itemLists.articles && itemLists.articles.length) || (isEditable && (itemLists.articles && itemLists.articles.length))"
          v-edit="'promotedContentAreaUpper'"
          :blocks="itemLists.articles"
          :custom-size="fixedSizeFullFirst"
          :force-row-size="'third'"
          :tracking="{
            listName: titleAutomatedContentUpper,
            listLength: undefined
          }"
          @open-folder-modal="openFolderModal"
        />

        <show-more
          v-if="moreApiUrls.articles"
          :api-url="moreApiUrls.articles"
          :tracking="{
            event: 'articleShowMore'
          }"
          @showMoreResult="showMoreAddToList('articles', ...arguments)"
        />
      </section>
      <section
        v-if="titleAutomatedContentLower.length || isEditable"
        class="content-area-wrapper wrap-container "
      >
        <sub-heading
          v-if="titleAutomatedContentLower || isEditable"
          :v-edit-heading="'titleAutomatedContentLower'"
          :heading="titleAutomatedContentLower"
        />

        <block-grid
          v-if="promotedContentAreaLower.length || (isEditable && promotedContentAreaLower.length)"
          v-edit="'promotedContentAreaLower'"
          :blocks="promotedContentAreaLower"
          :tracking="{
            listName: titleAutomatedContentLower,
            listLength: undefined
          }"
          @open-folder-modal="openFolderModal"
        />
      </section>
      <schema
        :headline="title"
        :image="image"
        :sizes="dimensions"
        :date-published="additionalInfo.datePublished"
        :date-modified="additionalInfo.dateModified"
        :cloudinary-base-url="$root.envConfig.cloudinaryBaseUrl"
      />
    </div>
    <ica-add-single-recipe-to-folder-modal
      v-if="$root.isLoggedIn"
      :active="showFolderModal"
      :recipe-id="activeRecipeId"
      :recipe-name="activeRecipeTitle"
      :get-access-token-fn="getPublicAccessToken"
      :recipe-base-url="$root.envConfig.recipeExperienceApiBaseUrl"
      @save-to-folder="saveToFolder"
      @close-modal="closeModal"
    />
  </div>
</template>
<script>
import PreambleBlock from '@/vue/blocks/PreambleBlock/preambleBlock'
import pageWithNavigation from '@/vue/mixins/pageWithNavigation'
import BylineShareComponent from '@/vue/components/bylineShareComponent/bylineShareComponent'
import IcaRelatedItems from "~/@ica/ica-molecules-vue/dist/RelatedItems"
import ContentRecipeBlock from '@/vue/blocks/ContentRecipeBlock/contentRecipeBlock'
import showMore from '@/vue/components/common/showMore'
import showMoreMixin from '@/vue/mixins/showMoreMixin'
import schema from '@/vue/components/common/pageSchema'
import recipeModalMixin from '@/vue/mixins/recipeModalMixin'

export default {
  name: "ArticlePage",
  components: {
    schema,
    PreambleBlock,
    BylineShareComponent,
    IcaRelatedItems,
    ContentRecipeBlock,
    showMore
  },
  mixins: [pageWithNavigation, showMoreMixin, recipeModalMixin],
  props: {
    brandingImage: {
      type: Object,
      default: null
    },
    prefix: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: ""
    },
    seoDescription: {
      type: String,
      default: ''
    },
    seoTitle: {
      type: String,
      default: ''
    },
    preamble: {
      type: String,
      default: ""
    },
    displayPreambleLink: {
      type: Boolean,
      default: false
    },
    preambleLinkLabel: {
      type: String,
      default: ''
    },
    preambleLinkPreLabel: {
      type: String,
      default: ''
    },
    preambleLink: {
      type: String,
      default: ""
    },
    supplierContentArea: {
      type: Array,
      default: () => []
    },
    contentAreaUpper: {
      type: Array,
      default: () => []
    },
    contentAreaLower: {
      type: Array,
      default: () => []
    },
    contentUpper: {
      type: Array,
      default: () => []
    },
    contentLower: {
      type: Array,
      default: () => []
    },
    similiarLinks: {
      type: Array,
      default: () => []
    },
    breadcrumbs: {
      type: Array,
      default () {
        return []
      }
    },
    author: {
      type: String,
      default: ""
    },
    authorDate: {
      type: String,
      default: ""
    },
    displayImagesInNavigationBlocks: {
      type: Boolean,
      default: false
    },
    image: {
      type: Object,
      default: null
    },
    titleRecipeContentArea: {
      type: String,
      default: ""
    },
    recipeContentArea: {
      type: Array,
      default () {
        return []
      }
    },
    titleAutomatedContentUpper: {
      type: String,
      default: ""
    },
    titleAutomatedContentLower: {
      type: String,
      default: ""
    },
    contentArea: {
      type: Array,
      default: () => []
    },
    promotedContentAreaUpper: {
      type: Array,
      default: () => []
    },
    promotedContentAreaLower: {
      type: Array,
      default: () => []
    },
    additionalInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      fixedSizeFullFirst: {
        isSmallViewport: {
          size: "full"
        }
      },
      dimensions: {
        small: {
          width: 375,
          height: 280
        },
        medium: {
          width: 768,
          height: 528
        },
        large: {
          width: 1400,
          height: 528
        }
      },
      brandingDimensions: {
        small: {
          height: 120,
          width: 120
        },
        medium: {
          height: 120,
          width: 120
        },
        large: {
          height: 176,
          width: 176
        }
      }
    }
  },
  computed: {
    aspectRatioMode () {
      return this.$root.currentSizeLte('sm')
    },
  }
}
</script>
<style lang="scss" >

.layout {
  &__info-content {
    @include media-breakpoint-up(md) {
      padding: 0 0 4rem;
    }

    @include media-breakpoint-up(xl) {
      padding-left: 2rem;
      padding-right: 2rem;
    }

    > div:not(:last-child) {
      margin-bottom: 2rem;

      @include media-breakpoint-up(md) {
        margin-bottom: 2.5rem;
      }
    }

    > div > *:first-child {
      margin-top: 0;
    }
  }
}

.article-page {
  padding-bottom: 4rem;

  // Remove all excessive bottom margin on the last section and its last child.
  // Instead let the article-page bottom padding rule
  > section:last-child,
  > section:last-child > *:last-child {
    margin-bottom: 0;
  }

  @include media-breakpoint-up(md) {
    width: 80rem;
    max-width: 100%;
    box-sizing: border-box;
    margin: 0 auto;
    padding-right: 1.5rem;
    padding-left: 1.5rem;
  }

  @include media-breakpoint-up(md) {
    > .wrap-container {
      padding: 0;
    }
  }

  &__hero-image {
    .epi-background-container {
      padding-bottom: 280px;
      border-radius: 0.5rem;

      @include media-breakpoint-up(md) {
        padding-bottom: 528px;
      }
    }
  }

  &__hero {
    background: $color-white;
    margin-bottom: 1rem;
    margin-top: -5.375rem;
    position: relative;
    padding: 2rem 0 0 0;
    margin-left: 1rem;
    margin-right: 1rem;
    border-radius: 4px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.06);

    @include media-breakpoint-up(md) {
      max-width: 100%;
      margin: 0;
      margin-top: -7.5rem;
      box-shadow: none;
      border-radius: 4px 4px 0 0;
      padding: 3.75rem 122px 0;
    }

    @include media-breakpoint-up(lg) {
      padding: 3.75rem 15.4375rem 0;
    }

    @include media-breakpoint-up(xl) {
      padding: 3.75rem 19.5rem 0;
    }
  }

  &__content {
    background-color: $color-white;
    padding: 2rem 0;
    margin-bottom: 2rem;
    border: 1px solid rgb(237, 237, 237);
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    border-left: 0;
    border-right: 0;

    @include media-breakpoint-up(md) {
      border: 0;
      box-shadow: none;
      padding: 0;
    }
  }

  // Generic margins. Has to be overriden if needed per component below.
  & &__content-area-upper,
  & &__content-area-lower {
    > *:not(:last-child):not(.accordion-block):not(.title-text-block) {
      margin-bottom: 2rem;

      @include media-breakpoint-up(md) {
        margin-bottom: 2.5rem;
      }
    }

    @include media-breakpoint-down(sm) {
      // Author block has top image sticking up 2.5rems above block.
      > * + .author-block {
        margin-top: 4.5rem;
      }
    }

    // Since accordion blocks in a row should have just a small margin
    .accordion-block + :not(.accordion-block) {
      margin-top: 2rem;

      @include media-breakpoint-up(md) {
        margin-top: 2.5rem;
      }
    }
    // This block could contain just a h3 or a h3 plus text. The margin to the next block differs depending on these facts.
    // If only h3 we have 0.5rem standard margin + the 0.5 below to a total of 1rem.
    // If text we have the 0.5 below + the wysiwyg 2 so a total of 2.5rem.
    > .title-text-block {
      margin-bottom: 0.5rem;

      // We add 2rem if wysiwyg is not empty
      .wysiwyg:not(:empty) {
        margin-bottom: 1.5rem;

        @include media-breakpoint-up(md) {
          margin-bottom: 2rem;
        }
      }
    }
  }

  .preamble__wrapper {
    margin: 0 0 3rem;
    padding: 0 1rem;

    @include media-breakpoint-up(md) {
      margin: 0 0 2.5rem;
      padding: 0;
    }
  }

  .preamble__link {
    display: inline-block;
  }

  .image-text-block {
    padding: 0;
  }

  .accordion-block,
  .article-recipe-block,
  .image-text-block,
  .title-text-block,
  .supplier-block,
  .container-list-block > h3 {
    margin-left: 2rem;
    margin-right: 2rem;

    @include media-breakpoint-up(md) {
      margin-left: 7.6875rem;
      margin-right: 7.6875rem;
    }

    @include media-breakpoint-up(lg) {
      margin-left: 15.4375rem;
      margin-right: 15.4375rem;
    }

    @include media-breakpoint-up(xl) {
      margin-left: 17.5rem;
      margin-right: 17.5rem;
    }
  }

  .author-block,
  .offer,
  .image-video,
  .fact-block,
  .container-list-block.box__type--Full {
    margin-left: 0.5rem;
    margin-right: 0.5rem;

    @include media-breakpoint-up(md) {
      margin-left: 3.875rem;
      margin-right: 3.875rem;
    }

    @include media-breakpoint-up(lg) {
      margin-left: 10.3125rem;
      margin-right: 10.3125rem;
    }

    @include media-breakpoint-up(xl) {
      margin-left: 11rem;
      margin-right: 11rem;
    }
  }

  .fact-block,
  .container-list-block.box__type--Full {
    padding: 3.5rem 1.5rem;

    @include media-breakpoint-up(md) {
      padding: 4rem 3.75rem;
    }

    @include media-breakpoint-up(lg) {
      padding: 4rem 5rem;
    }

    @include media-breakpoint-up(xl) {
      padding: 4rem 6.5rem;
    }
  }

  .accordion-block {
    box-shadow: none;
    border: 1px solid $color-grey-special;
    border-radius: 4px;

    &__content-wrapper {
      border-top: 1px solid $color-grey-special;
    }
  }

  .offer {
    border: 0.0625rem solid $color-grey-special;

    &__content {
      text-align: left;
      min-height: 0;

      h3 {
        max-width: calc(100% - 2rem);

        @include media-breakpoint-up(md) {
          max-width: calc(100% - 8rem);
        }
      }

      .offer__rules {
        margin-bottom: 0;
      }
    }

    &__content-wrapper {
      padding: 1rem;
      flex-direction: row;
    }

    &__image-wrapper {
      margin-bottom: 0;

      @include media-breakpoint-up(md) {
        margin-right: 2rem;
        min-width: 10rem;
      }

      .epi-picture {
        text-align: center;

        img {
          max-height: 10rem;
        }
      }
    }

    .button--text-icon {
      margin: 0;
      width: auto;
    }
  }
}

.image-wrap {
  max-width: 1400px;
  margin: 0 auto;
  position: relative;

  .empty-image-placeholder {
    min-height: 17.5rem;

    @include media-breakpoint-up(md) {
      min-height: 33rem;
    }

    @include media-breakpoint-up(lg) {
      min-height: 33.9375rem;
    }
  }

  &__sub-branding.epi-picture {
    width: 7.5rem;
    position: absolute;
    top: 1rem;
    right: 1rem;

    @include media-breakpoint-up(sm) {
      top: 1.5rem;
      right: 1.5rem;
    }

    @include media-breakpoint-up(lg) {
      width: 11rem;
    }
  }
}

.related-items-section {
  margin-bottom: 3rem;
  margin-top: 3rem;
  padding: 0 1rem;

  .related-items__item,
  .related-items__item:not(:last-child) {
    margin: 0.5rem 0.5rem 0 0;
  }

  &__title {
    @include font(rubrik, 2, "bold");

    margin: 0 0 -0.5rem 0;
    color: $color-brown-dark;
  }
}

</style>
